import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalBody,
  ModalHeader,
  Col,
  Row,
  Button,
  Spinner,
} from "reactstrap";
import { X } from "react-feather";
import CustomSelect from "components/customs/dropdown";
import { Toast } from "utility/helpers";
import { genericPost, genericPut } from "services/services.general";
import PropTypes from "prop-types";
import styled from "styled-components";
import { AddButton } from "assets/js/Styles";
import { ADD_ACTION, EDIT_ACTION, VIEW_ACTION } from "constants/actions";
import { addProjectDropdownStyles } from "constants/style";
import FormGenerator from "components/customs/form-generator/input";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getModalHeader, validateCommitteeForm } from "./utility";

const CancelButton = styled(AddButton)`
  border-color: ${({ theme }) => theme.colors.buttonFormRed};
  background: white;
  width: 130px;
  color: ${({ theme }) => theme.colors.buttonFormRed};
  font-weight: 700;
  border-radius: 13px;

  &:hover {
    background: transparent;
    color: black;
  }
`;

const SaveButton = styled(AddButton)`
  background: ${({ theme }) => theme.colors.buttonFormBlue};
  width: 130px;
  font-weight: 700;
  border-radius: 13px;

  &:hover {
    background: transparent;
    border: 1px solid ${({ theme }) => theme.colors.buttonFormBlue};
    color: black;
  }
`;

const AddCommitteeButton = styled(Button)`
  background: transparent;
  border: 1px dashed ${({ theme }) => theme.colors.blue50};
  border-radius: 4px;
  height: clamp(35px, 5vw, 50px);
  width: 100%;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.blue50};
  font-weight: 400;
  margin-top: 24px;

  &:hover {
    background: transparent;
    color: black;
  }
`;

const RemoveButton = styled(Button)`
  background: transparent;
  border: none;
  color: #df3348;
  padding: 0;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: transparent;
    color: #df3348;
  }
`;

const CommitteePositionRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 1rem;
  align-items: end;
  margin-bottom: 1rem;
`;

const ModalButtonGroup = styled.div`
  display: flex;
  justify-content: space-between;
  margin-top: 60px;
`;

const ModalLabel = styled.span`
  margin-bottom: 9px;
  font-weight: 400;
  color: ${({ theme }) => theme.colors.blue70};
`;

const ModalFormRow = styled(Row)`
  margin-bottom: 1.5rem;
`;

const DatePickerWrapper = styled.div`
  width: 100%;

  .react-datepicker-wrapper {
    width: 100%;
  }
`;

const StyledDatePicker = styled(DatePicker)`
  height: 50px;
  border: 2px solid ${({ theme }) => theme.colors.black100};
  background: transparent;
  border-radius: 13px;
  padding: 2px 10px;
  padding-bottom: 4px;
  padding-top: 2px;
  width: 100%;

  &:focus {
    box-shadow: none;
    border-color: ${({ theme }) => theme.colors.systemBlue};
  }
`;

const StyledCol = styled(CommitteePositionRow)`
  grid-template-columns: 1fr 1fr;
`;

const CommitteeModal = ({
  isOpen,
  handleClose,
  houseMembers,
  regions,
  districts,
  affiliations,
  committees,
  positions,
  fetchDataTable,
  mode = ADD_ACTION, // 'add', 'edit', 'view'
  initialData = null,
}) => {
  const modalBodyRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [formHouseMember, setFormHouseMember] = useState(null);
  const [formRegion, setFormRegion] = useState(null);
  const [formDistrict, setFormDistrict] = useState(null);
  const [formAffiliation, setFormAffiliation] = useState(null);
  const [formDateElected, setFormDateElected] = useState(null);
  const [formSource, setFormSource] = useState("");

  const [committeePositions, setCommitteePositions] = useState([
    { id: Date.now(), committee: null, position: null },
  ]);

  useEffect(() => {
    if (isOpen) {
      // If initialData is provided (edit/view mode), populate form
      if (initialData && mode !== ADD_ACTION) {
        const { congressman } = initialData;
        const committee = initialData.committee || initialData.Committee;
        const { position } = initialData;

        // Set House Member
        if (congressman) {
          const houseMemberOption = houseMembers.find(
            (hm) => hm.value === congressman.id
          ) || {
            value: congressman.id,
            label: congressman.fullName || congressman.name,
            data: congressman,
          };
          setFormHouseMember(houseMemberOption);

          // Set Region
          if (congressman.district?.region) {
            setFormRegion({
              value: congressman.district.region.id,
              label: congressman.district.region.name,
            });
          }

          // Set District
          if (congressman.district) {
            setFormDistrict({
              value: congressman.district.id,
              label: congressman.district.name || congressman.district.district,
            });
          }

          // Set Affiliation
          if (congressman.party) {
            setFormAffiliation({
              value: congressman.party.id,
              label: congressman.party.name || congressman.party.acronym,
            });
          }
        }

        // Set Committee & Position
        if (committee && position) {
          setCommitteePositions([
            {
              id: Date.now(),
              committee: {
                value: committee.id,
                label: committee.name,
              },
              position: {
                value: position.id,
                label: position.name,
              },
            },
          ]);
        }

        // Set Date Elected and Source
        // (handle null/undefined for backward compatibility)
        if (initialData.dateElected) {
          // Convert string date to Date object for FormGenerator
          const dateObj = new Date(initialData.dateElected);
          setFormDateElected(isNaN(dateObj.getTime()) ? null : dateObj);
        } else {
          setFormDateElected(null);
        }

        if (initialData.source) {
          setFormSource(initialData.source);
        } else {
          setFormSource("");
        }
      } else {
        // Reset form when opening in create mode
        setFormHouseMember(null);
        setFormRegion(null);
        setFormDistrict(null);
        setFormAffiliation(null);
        setFormDateElected(null);
        setFormSource("");

        setCommitteePositions([
          { id: Date.now(), committee: null, position: null },
        ]);
      }
    }
    // Removed houseMembers from dependencies to prevent unnecessary re-runs
  }, [isOpen, initialData, mode]);

  const handleAddCommitteePosition = () => {
    setCommitteePositions([
      ...committeePositions,
      { id: Date.now(), committee: null, position: null },
    ]);
  };

  const handleRemoveCommitteePosition = (index) => {
    if (committeePositions.length > 1) {
      const newPositions = committeePositions.filter((_, i) => i !== index);

      setCommitteePositions(newPositions);
    }
  };

  const handleUpdateCommitteePosition = (index, field, value) => {
    const newPositions = [...committeePositions];

    newPositions[index] = {
      ...newPositions[index],
      [field]: value,
    };
    setCommitteePositions(newPositions);
  };

  const handleHouseMemberChange = (option) => {
    setFormHouseMember(option);

    // Auto-fill Region, District, and Affiliation from House Member data
    if (option?.data) {
      const houseMemberData = option.data;
      // Handle both lowercase and capitalized property names
      const region = houseMemberData.region || houseMemberData.Region;
      const district = houseMemberData.district || houseMemberData.District;
      const party = houseMemberData.party || houseMemberData.Party;

      if (region) {
        setFormRegion({
          value: region.id,
          label: region.name,
        });
      }

      if (district) {
        setFormDistrict({
          value: district.id,
          label: district.name || district.district,
        });
      }

      if (party) {
        setFormAffiliation({
          value: party.id,
          label: party.name,
        });
      }
    }
  };

  const handleSave = () => {
    const validCommittees = committeePositions.filter(
      (cp) => cp.committee && cp.position && cp.position.value
    );

    const isValid = validateCommitteeForm(
      formHouseMember,
      formRegion,
      formAffiliation,
      validCommittees
    );

    if (typeof isValid === "string") {
      Toast({
        type: 4,
        content: isValid,
      });

      return;
    }

    setIsLoading(true);

    // For edit mode, use PUT with single endpoint
    if (mode === EDIT_ACTION && initialData?.id) {
      // Format date to YYYY-MM-DD for backend
      const formattedDate = formDateElected
        ? formDateElected.toISOString().split("T")[0]
        : null;

      const payload = {
        CongressmanId: formHouseMember.value,
        CommitteeId: validCommittees[0].committee.value,
        PositionId: validCommittees[0].position.value,
        dateElected: formattedDate,
        source: formSource || null,
      };

      genericPut({
        url: `/membership-committee/${initialData.id}`,
        formData: payload,
        callback: ({ isSuccess }) => {
          setIsLoading(false);

          if (isSuccess) {
            Toast({
              type: 1,
              content: "Committee assignment updated successfully.",
            });
            handleClose();

            if (fetchDataTable) {
              fetchDataTable();
            }
          } else {
            Toast({
              type: 4,
              content:
                "This position in this committee for this congressman already exists.",
            });
          }
        },
      });
    } else {
      // Create mode
      const isSingle = validCommittees.length === 1;

      const url = isSingle
        ? "/membership-committee"
        : "/membership-committee/batch-create";

      // Format date to YYYY-MM-DD for backend
      const formattedDate = formDateElected
        ? formDateElected.toISOString().split("T")[0]
        : null;

      const payload = isSingle
        ? {
            CongressmanId: formHouseMember.value,
            CommitteeId: validCommittees[0].committee.value,
            PositionId: validCommittees[0].position.value,
            dateElected: formattedDate,
            source: formSource || null,
          }
        : validCommittees.map((cp) => ({
            CongressmanId: formHouseMember.value,
            CommitteeId: cp.committee.value,
            PositionId: cp.position.value,
            dateElected: formattedDate,
            source: formSource || null,
          }));

      genericPost({
        url,
        formData: payload,
        callback: ({ isSuccess }) => {
          setIsLoading(false);

          if (isSuccess) {
            Toast({
              type: 1,
              content: "Committee assignment created successfully.",
            });
            handleClose();

            if (fetchDataTable) {
              fetchDataTable();
            }
          } else {
            Toast({
              type: 4,
              content:
                "This position in this committee for this congressman already exists.",
            });
          }
        },
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      centered
      size="lg"
    >
      <ModalHeader toggle={handleClose}>{getModalHeader(mode)}</ModalHeader>
      <ModalBody ref={modalBodyRef}>
        <ModalFormRow>
          <Col xs="12">
            <ModalLabel>{"House Member"}</ModalLabel>
            <CustomSelect
              placeholder="Select House Member"
              isDisabled={mode === VIEW_ACTION}
              value={formHouseMember}
              onChange={handleHouseMemberChange}
              options={houseMembers}
              styles={addProjectDropdownStyles}
            />
          </Col>
        </ModalFormRow>

        <ModalFormRow>
          <Col xs="12">
            <ModalLabel>{"Region"}</ModalLabel>
            <CustomSelect
              placeholder="Select Region"
              isDisabled
              value={formRegion}
              onChange={(option) => setFormRegion(option)}
              options={regions}
              styles={addProjectDropdownStyles}
            />
          </Col>
        </ModalFormRow>

        <ModalFormRow>
          <Col xs="12">
            <ModalLabel>{"District/Party-list"}</ModalLabel>
            <CustomSelect
              placeholder="Select District"
              isDisabled
              value={formDistrict}
              onChange={(option) => setFormDistrict(option)}
              options={districts}
              styles={addProjectDropdownStyles}
            />
          </Col>
        </ModalFormRow>

        <ModalFormRow>
          <Col xs="12">
            <ModalLabel>{"Affiliation"}</ModalLabel>
            <CustomSelect
              placeholder="Select Affiliation"
              isDisabled
              value={formAffiliation}
              onChange={(option) => setFormAffiliation(option)}
              options={affiliations}
              styles={addProjectDropdownStyles}
            />
          </Col>
        </ModalFormRow>

        <ModalFormRow>
          <StyledCol>
            <div>
              <ModalLabel>{"Date Elected"}</ModalLabel>
              <DatePickerWrapper>
                <StyledDatePicker
                  selected={formDateElected}
                  placeholderText="Select Date Elected"
                  dateFormat="MMM-dd-yyyy"
                  onChange={(date) => {
                    setFormDateElected(date);
                  }}
                  disabled={mode === VIEW_ACTION}
                />
              </DatePickerWrapper>
            </div>
            <div>
              <ModalLabel>{"Source"}</ModalLabel>
              <FormGenerator
                type="text"
                placeholder="Enter Source"
                value={formSource}
                isDisabled={mode === VIEW_ACTION}
                onChange={(e) => setFormSource(e.target.value)}
              />
            </div>
          </StyledCol>
        </ModalFormRow>

        <ModalFormRow>
          <Col xs="12">
            {committeePositions.map((cp, index) => (
              <CommitteePositionRow key={cp.id}>
                <div>
                  <ModalLabel>{"Committee"}</ModalLabel>
                  <CustomSelect
                    placeholder="Select Committee"
                    isDisabled={mode === VIEW_ACTION}
                    value={cp.committee}
                    onChange={(option) =>
                      handleUpdateCommitteePosition(index, "committee", option)
                    }
                    options={committees || []}
                    styles={addProjectDropdownStyles}
                  />
                </div>
                <div>
                  <ModalLabel>{"Position"}</ModalLabel>
                  <CustomSelect
                    placeholder="Select Position"
                    isDisabled={mode === VIEW_ACTION}
                    value={cp.position}
                    onChange={(option) =>
                      handleUpdateCommitteePosition(index, "position", option)
                    }
                    options={positions}
                    styles={addProjectDropdownStyles}
                  />
                </div>
                {committeePositions.length > 1 && mode === ADD_ACTION && (
                  <RemoveButton
                    onClick={() => handleRemoveCommitteePosition(index)}
                  >
                    <X size={20} />
                  </RemoveButton>
                )}
              </CommitteePositionRow>
            ))}

            {mode === ADD_ACTION && (
              <AddCommitteeButton onClick={handleAddCommitteePosition}>
                {"Add Committee"}
              </AddCommitteeButton>
            )}
          </Col>
        </ModalFormRow>

        <ModalButtonGroup>
          <CancelButton
            onClick={handleClose}
            disabled={isLoading}
            outline
          >
            {"Cancel"}
          </CancelButton>
          {mode !== VIEW_ACTION && (
            <SaveButton
              color="primary"
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? (
                <Spinner
                  type="border"
                  color="dark"
                  size="sm"
                />
              ) : (
                "Save"
              )}
            </SaveButton>
          )}
        </ModalButtonGroup>
      </ModalBody>
    </Modal>
  );
};

CommitteeModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  houseMembers: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  regions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  districts: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  affiliations: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  committees: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  positions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    })
  ),
  fetchDataTable: PropTypes.func,
  mode: PropTypes.oneOf([ADD_ACTION, EDIT_ACTION, VIEW_ACTION]),
  initialData: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    congressman: PropTypes.shape({}),
    committee: PropTypes.shape({}),
    position: PropTypes.shape({}),
  }),
};

CommitteeModal.defaultProps = {
  houseMembers: [],
  regions: [],
  districts: [],
  affiliations: [],
  committees: [],
  positions: [],
  fetchDataTable: null,
  mode: ADD_ACTION,
  initialData: null,
};

export default CommitteeModal;
